import React, { useState, useRef, useEffect } from 'react';

export interface DropdownOption {
  id: string | number;
  name: string;
}

interface CustomDropdownProps {
  label?: string;
  placeholder?: string;
  options: (DropdownOption | string)[];
  value: string | string[];
  onChange: (val: any) => void;
  isMulti?: boolean;
  searchable?: boolean;
  showOkButton?: boolean;
  unitLabel?: string;
  onOk?: () => void;
}

export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  label,
  placeholder = 'Select option',
  options,
  value,
  onChange,
  isMulti = false,
  searchable = true,
  showOkButton = false,
  unitLabel,
  onOk,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Normalize options to array of { id, name }
  const normalizedOptions: DropdownOption[] = options.map((opt, idx) =>
    typeof opt === 'string' ? { id: `${opt}-${idx}`, name: opt } : opt
  );

  const actualOptions = normalizedOptions.filter(
    (o) => !o.name.startsWith('Select All') && !o.name.startsWith('Select all')
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = normalizedOptions.filter((opt) =>
    opt.name.toLowerCase().includes(search.toLowerCase())
  );

  const isSelected = (optName: string) => {
    if (!Array.isArray(value)) {
      return value === optName;
    }
    if (optName.startsWith('Select All') || optName.startsWith('Select all')) {
      return (
        actualOptions.length > 0 &&
        actualOptions.every((o) => value.includes(o.name))
      );
    }
    return value.includes(optName);
  };

  const handleToggle = (optName: string) => {
    if (isMulti) {
      const current = Array.isArray(value) ? value : [];
      if (optName.startsWith('Select All') || optName.startsWith('Select all')) {
        const allActualSelected =
          actualOptions.length > 0 && actualOptions.every((o) => current.includes(o.name));
        if (allActualSelected) {
          onChange([]);
        } else {
          onChange([optName, ...actualOptions.map((o) => o.name)]);
        }
        return;
      }

      const nextRaw = current.includes(optName)
        ? current.filter((v) => v !== optName)
        : [...current, optName];

      const actualSelectedCount = actualOptions.filter((o) => nextRaw.includes(o.name)).length;
      const isAllActualSelected = actualOptions.length > 0 && actualSelectedCount === actualOptions.length;

      let next = nextRaw;
      if (!isAllActualSelected) {
        next = nextRaw.filter((v) => !v.startsWith('Select All') && !v.startsWith('Select all'));
      } else {
        const selectAllName = selectAllOption ? selectAllOption.name : 'Select All';
        if (!next.includes(selectAllName)) {
          next = [selectAllName, ...next];
        }
      }
      onChange(next);
    } else {
      onChange(optName);
      setIsOpen(false);
    }
  };

  const selectAllOption = normalizedOptions.find(
    (o) => o.name.startsWith('Select All') || o.name.startsWith('Select all')
  );

  const displayValue = () => {
    if (isMulti) {
      const arr = Array.isArray(value)
        ? value.filter((v) => !v.startsWith('Select All') && !v.startsWith('Select all'))
        : [];
      if (arr.length === 0) {
        return placeholder;
      }
      if (actualOptions.length > 0 && arr.length === actualOptions.length) {
        return selectAllOption
          ? selectAllOption.name.replace(/^Select /i, 'Selected ')
          : 'All Selected';
      }
      if (arr.length === 1) return arr[0];
      if (arr.length > 1) {
        const labelText = unitLabel ? `${unitLabel} Selected` : 'Selected';
        return `${arr.length} ${labelText}`;
      }
    }
    return (value as string) || placeholder;
  };

  const selectedCount = Array.isArray(value)
    ? value.filter((v) => !v.startsWith('Select All') && !v.startsWith('Select all')).length
    : 0;

  return (
    <div className="space-y-1.5 relative" ref={containerRef}>
      {label && <label className="block text-xs font-semibold text-gray-700 mb-1.5">{label}</label>}

      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between border border-gray-300 focus:border-[#B71C1C] focus:ring-1 focus:ring-[#B71C1C] outline-none text-gray-700 text-xs md:text-sm rounded-xl py-2.5 px-3 bg-white cursor-pointer hover:border-gray-400 transition-colors"
      >
        <span className="truncate pr-2 font-medium">{displayValue()}</span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden flex flex-col animate-fadeIn">
          {/* Search Box */}
          {searchable && (
            <div className="bg-gray-50 border-b border-gray-100 flex items-center shrink-0">
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent outline-none text-xs font-medium text-gray-800 placeholder-gray-400 px-3.5 py-2"
              />
            </div>
          )}

          {/* Options List */}
          <div className="overflow-y-auto flex-1 p-1 space-y-0.5 max-h-52 scrollbar-thin">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const checked = isSelected(opt.name);
                return (
                  <label
                    key={opt.id}
                    onClick={() => {
                      if (!isMulti) {
                        handleToggle(opt.name);
                      }
                    }}
                    className="flex items-center gap-2.5 px-3 py-2 hover:bg-red-50/50 rounded-lg cursor-pointer select-none transition-colors"
                  >
                    {isMulti ? (
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleToggle(opt.name)}
                        className="rounded border-gray-300 focus:ring-[#B71C1C] w-4 h-4 cursor-pointer accent-[#B71C1C]"
                      />
                    ) : (
                      <span
                        className={`w-2 h-2 rounded-full shrink-0 ${
                          checked ? 'bg-[#B71C1C]' : 'bg-transparent'
                        }`}
                      />
                    )}
                    <span
                      className={`text-xs ${
                        checked ? 'font-semibold text-[#B71C1C]' : 'font-medium text-gray-700'
                      }`}
                    >
                      {opt.name}
                    </span>
                  </label>
                );
              })
            ) : (
              <div className="text-center text-xs text-gray-400 py-3">No options found</div>
            )}
          </div>

          {/* Fixed Footer with OK button for Multi-select */}
          {isMulti && showOkButton && (
            <div className="p-2 border-t border-gray-100 bg-gray-50 flex items-center justify-between shrink-0">
              <span className="text-[11px] text-gray-500 font-medium px-2">
                {selectedCount > 0 ? `${selectedCount} selected` : placeholder}
              </span>
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  if (onOk) onOk();
                }}
                className="bg-[#B71C1C] hover:bg-[#960101] text-white text-xs font-semibold py-1.5 px-4 rounded-lg shadow-sm transition-colors cursor-pointer"
              >
                OK
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


